import {InputDate} from '@/components/commons/input-date/InputDate'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {InputTime} from '@/components/commons/input-time/InputTime'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {AgendaEventNotesField} from '@/features/agenda/components/agenda-event-form/agenda-event-notes-field/AgendaEventNotesField'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {
    accessTypeCategoryToEventCategory,
    accessTypeKeyNameToDefaultDuration,
    agendaDurationMapToLabel
} from '@/features/agenda/utils'
import {InputSelectPatient} from '@/features/patients/components/input-select-patient/InputSelectPatient'
import {InputSelectPlannedAccess} from '@/features/planned-accesses/components/input-select-planned-access/InputSelectPlannedAccess'
import {InputSelectUser} from '@/features/users/components/input-select-user/InputSelectUser'
import {useTranslation} from '@/translations/i18n'
import {ObjectEntries} from '@/utilities/helpers'
import {useController, useFormContext} from 'react-hook-form'
import {z} from 'zod'

const ExternalStructureEventFieldsSchema = AgendaEventFieldsSchema.pick({
    patients: true,
    consultants: true,
    category: true,
    duration: true,
    date: true,
    note: true
})
type ExternalStructureEventFieldsSchema = z.infer<typeof ExternalStructureEventFieldsSchema>

/**
 * Fields of the reduced external structure event form, rendered by both the create and the edit dialog in
 * place of the standard event fields. The structure is picked among the users flagged as external
 * structures; the event category is derived from the selected access type, since the form does not expose
 * a category selector.
 */
export const ExternalStructureEventFields = () => {
    const {t} = useTranslation()
    const {control, setValue, clearErrors} = useFormContext<ExternalStructureEventFieldsSchema>()

    const {field: patientsField, fieldState: patientsFieldState} = useController({control, name: 'patients'})
    const {field: structuresField, fieldState: structuresFieldState} = useController({control, name: 'consultants'})
    const {field: accessTypeField, fieldState: accessTypeFieldState} = useController({
        control,
        name: 'consultants.0.accessType'
    })
    const {field: durationField, fieldState: durationFieldState} = useController({control, name: 'duration'})
    const {field: dateField, fieldState: dateFieldState} = useController({control, name: 'date.date'})
    const {field: timeField, fieldState: timeFieldState} = useController({control, name: 'date.time'})

    const patient = patientsField.value?.[0]
    const structure = structuresField.value?.[0]

    return (
        <Flexbox direction="column" gap={6} fullWidth>
            <InputSelectPatient
                label={t('commons:patient')}
                placeholder={t('commons:select_patient')}
                value={patient}
                hideBadge
                errorMessage={patientsFieldState.error?.message}
                onChange={selectedPatient => {
                    patientsField.onChange(
                        selectedPatient
                            ? [
                                  {
                                      id: selectedPatient.id,
                                      fullName: selectedPatient.fullName,
                                      treatmentRequestId: selectedPatient.treatmentRequestId,
                                      neuromuscularDiagnosisId: selectedPatient.neuromuscularDiagnosisId,
                                      // External structure events are not tied to a project
                                      project: null
                                  }
                              ]
                            : []
                    )
                }}
            />

            <Grid columns={2} columnGap={4} rowGap={6} fullWidth>
                <InputSelectUser
                    label={t('agenda:external_structure')}
                    placeholder={t('agenda:select_external_structure')}
                    value={structure ?? null}
                    filters={{isExternalStructure: true}}
                    errorMessage={structuresFieldState.error?.message}
                    onChange={selectedStructure => {
                        structuresField.onChange(
                            selectedStructure
                                ? [
                                      {
                                          id: selectedStructure.id,
                                          fullName: selectedStructure.fullName,
                                          specializationName: selectedStructure.specializationName,
                                          specializationId: selectedStructure.specializationId,
                                          isExternalStructure: selectedStructure.isExternalStructure,
                                          // Access types are filtered by the structure specialization, so the
                                          // previous selection no longer applies, and neither do the values
                                          // derived from it
                                          accessType: null
                                      }
                                  ]
                                : []
                        )
                        setValue('category', null)
                        setValue('duration', null)
                    }}
                />

                <InputSelectPlannedAccess
                    label={t('commons:access_type')}
                    placeholder={t('agenda:select_access_type_placeholder')}
                    value={accessTypeField.value}
                    disabled={!structure?.id}
                    specializationId={structure?.specializationId}
                    errorMessage={accessTypeFieldState.error?.message}
                    onChange={value => {
                        accessTypeField.onChange(value)
                        if (!value) {
                            return
                        }
                        // The category is not user facing here: it follows the selected access type
                        setValue('category', accessTypeCategoryToEventCategory(value.category), {shouldDirty: true})
                        setValue('duration', accessTypeKeyNameToDefaultDuration[value.accessType].toString(), {
                            shouldDirty: true
                        })
                        clearErrors('duration')
                    }}
                />
            </Grid>

            <Grid columns={3} columnGap={4} rowGap={6} fullWidth>
                <InputDate
                    label={t('commons:date')}
                    placeholder="GG/MM/AAAA"
                    fullWidth
                    value={dateField.value ?? null}
                    errorMessage={dateFieldState.error?.message ? t('errors:required') : undefined}
                    onChange={value => {
                        dateField.onChange(value)
                        if (value) {
                            clearErrors('date')
                        }
                    }}
                />

                <InputTime
                    label={t('agenda:start_time')}
                    placeholder={t('agenda:select_time')}
                    fullWidth
                    value={timeField.value}
                    errorMessage={timeFieldState.error?.message}
                    startTime="08:00"
                    endTime="18:45"
                    interval={15}
                    onChange={timeField.onChange}
                />

                <InputSelect
                    label={t('commons:duration')}
                    value={durationField.value ?? undefined}
                    errorMessage={durationFieldState.error?.message}
                    optionsEntries={ObjectEntries(agendaDurationMapToLabel).map(([key, value]) => [
                        key.toString(),
                        t(value, {value: key}).toString()
                    ])}
                    onChange={durationField.onChange}
                />
            </Grid>

            <AgendaEventNotesField />
        </Flexbox>
    )
}
